import { useEffect, useMemo, useReducer, useRef } from "react";
import { buildWorkspaceNavigationItems, getWorkspaceNavigationLabel } from "../lib/workspace-navigation";
import type { WorkspaceNavigationAccess } from "../lib/workspace-navigation";

export function workspaceSwitcherReducer(open: boolean, action: "toggle" | "close" | "escape" | "select"): boolean {
  if (action === "toggle") return !open;
  return false;
}

export default function WorkspaceSwitcher({ path, access, navigate }: {
  path: string;
  access: WorkspaceNavigationAccess;
  navigate: (path: string) => void;
}) {
  const [open, dispatch] = useReducer(workspaceSwitcherReducer, false);
  const menuRef = useRef<HTMLDivElement>(null);
  const currentLabel = getWorkspaceNavigationLabel(path);
  const destinations = useMemo(
    () => buildWorkspaceNavigationItems(access).filter((item) => item.label !== currentLabel),
    [access, currentLabel],
  );

  useEffect(() => dispatch("close"), [path]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) dispatch("close");
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") dispatch("escape");
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  function select(pathname: string) {
    dispatch("select");
    navigate(pathname);
  }

  return (
    <div className="workspace-switcher" ref={menuRef}>
      <button
        className="workspace-switcher__toggle"
        type="button"
        aria-label={`${currentLabel} workspace navigation`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={destinations.length === 0}
        onClick={() => dispatch("toggle")}
      >
        {currentLabel}<span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="workspace-switcher__menu" role="menu">
          {destinations.map((item) => (
            <button key={item.id} className="workspace-switcher__item" type="button" role="menuitem" onClick={() => select(item.path)}>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
